import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, Check, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RedeemDeal = () => {
  const { code } = useParams();
  const [token, setToken] = useState(
    localStorage.getItem('role') === 'admin' ? localStorage.getItem('token') : null
  );
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [error, setError] = useState(null);

  // inline admin login
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  const lookup = async (authToken) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/redeem/${code}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setInfo(res.data);
    } catch (e) {
      if (e.response?.status === 401) {
        setToken(null);
        localStorage.removeItem('token');
        localStorage.removeItem('role');
      } else {
        setError(e.response?.data?.detail || 'Failed to look up reward');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) lookup(token);
    // eslint-disable-next-line
  }, [token]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('role', 'admin');
      setToken(res.data.token);
    } catch (err) {
      toast.error('Invalid admin credentials');
    } finally {
      setLoggingIn(false);
    }
  };

  const confirmRedeem = async () => {
    setRedeeming(true);
    try {
      await axios.post(`${API}/admin/redeem/${code}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Reward redeemed!');
      setInfo({ ...info, redeemed: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to redeem');
      lookup(token);
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-sm">
        {/* Admin login gate */}
        {!token && (
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={doLogin}
            className="bg-[#18181b] border border-[#27272a] rounded-3xl p-6 space-y-4"
            data-testid="redeem-admin-login"
          >
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#A855F7]" />
              <h1 className="text-lg font-heading font-bold text-white">Admin Sign In</h1>
            </div>
            <p className="text-sm text-[#a1a1aa]">Sign in to verify this reward.</p>
            <Input
              data-testid="redeem-username"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="bg-[#09090b] border-[#27272a] text-white"
            />
            <Input
              data-testid="redeem-password"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#09090b] border-[#27272a] text-white"
            />
            <Button
              data-testid="redeem-login-btn"
              type="submit"
              disabled={loggingIn}
              className="w-full bg-[#A855F7] hover:bg-[#9333EA] text-white font-bold rounded-full h-11"
            >
              {loggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign In'}
            </Button>
          </motion.form>
        )}

        {/* Reward info */}
        {token && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#18181b] border border-[#27272a] rounded-3xl p-6 text-center"
            data-testid="redeem-info"
          >
            {loading ? (
              <Loader2 className="w-8 h-8 text-[#EAB308] animate-spin mx-auto" />
            ) : error ? (
              <div data-testid="redeem-error">
                <XCircle className="w-12 h-12 text-[#f43f5e] mx-auto mb-3" />
                <p className="text-white font-medium">{error}</p>
              </div>
            ) : info ? (
              <>
                <div className="bg-[#EAB308]/15 w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Gift className="w-7 h-7 text-[#EAB308]" />
                </div>
                <p className="text-xs uppercase tracking-widest text-[#52525b]">Reward Code {info.code}</p>
                <h1 className="text-xl font-heading font-bold text-white mt-1 mb-2">
                  {info.deal_text || 'Deal Reward'}
                </h1>
                {info.poll_question && (
                  <p className="text-sm text-[#a1a1aa] mb-5">From poll: “{info.poll_question}”</p>
                )}

                {info.redeemed ? (
                  <div
                    className="flex flex-col items-center gap-2 bg-[#27272a] rounded-2xl py-5"
                    data-testid="redeem-already"
                  >
                    <Check className="w-8 h-8 text-[#10b981]" />
                    <p className="text-white font-bold">Already Redeemed</p>
                    {info.redeemed_at && (
                      <p className="text-xs text-[#52525b]">
                        {new Date(info.redeemed_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                ) : (
                  <Button
                    data-testid="redeem-confirm-btn"
                    onClick={confirmRedeem}
                    disabled={redeeming}
                    className="w-full bg-[#10b981] hover:bg-[#059669] text-black font-bold rounded-full h-12"
                  >
                    {redeeming ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" /> Confirm & Redeem
                      </>
                    )}
                  </Button>
                )}
              </>
            ) : null}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default RedeemDeal;
